import React, { useState } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Collapse,
  useMediaQuery,
  useTheme as useMuiTheme,
  Avatar,
  Badge,
} from '@mui/material';
import {
  Home,
  Users,
  FileText,
  ChevronDown,
  ChevronRight,
  Shield,
  Building,
  AlertTriangle,
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';

const Sidebar = ({ open, onToggle, width, collapsedWidth, isMobile }) => {
  const { user, isAdmin } = useAuth();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const muiTheme = useMuiTheme();
  const isSmallScreen = useMediaQuery(muiTheme.breakpoints.down('md'));

  const [certificatesExpanded, setCertificatesExpanded] = useState(true);

  // Modern color palette
  const colors = {
    primary: '#3b82f6',
    secondary: '#8b5cf6',
    success: '#10b981',
    warning: '#f59e0b',
    error: '#ef4444',
    info: '#06b6d4',
    sync: '#6366f1',
  };

  // Navigation items based on user role
  const getNavigationItems = () => {
    const items = [
      {
        id: 'dashboard',
        label: 'Dashboard',
        icon: Home,
        path: '/dashboard',
        color: colors.primary,
      },
    ];

    // Admin-only items
    if (isAdmin()) {
      items.push({
        id: 'users',
        label: 'User Management',
        icon: Users,
        path: '/users',
        color: colors.secondary,
        badge: '3',
      });
    }

    // Certificate management for both roles
    items.push({
      id: 'certificates',
      label: 'Certificates',
      path: '/certificates',
      icon: FileText,
      color: colors.info,
    });

    // Certificates without attachments (for both Admin and Office)
    items.push({
      id: 'certs-without-attachments',
      label: 'Certs Without Attachments',
      path: '/certificates-without-attachments',
      icon: AlertTriangle,
      color: colors.warning,
    });

    // ADMIN ONLY: Sync Management
    if (isAdmin()) {
      items.push({
        id: 'sync-certificates',
        label: 'Sync Certificates',
        path: '/sync',
        icon: FileText,
        color: colors.sync,
        badge: 'Admin',
      });
    }

    return items;
  };

  const navigationItems = getNavigationItems();

  const handleNavigate = path => {
    navigate(path);
    if (isMobile) {
      onToggle();
    }
  };

  const isActivePath = path => {
    return location.pathname === path;
  };

  const renderNavigationItem = (item, isChild = false) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded =
      item.id === 'certificates' ? certificatesExpanded : false;
    const isActive = isActivePath(item.path);

    return (
      <React.Fragment key={item.id}>
        <ListItem disablePadding sx={{ display: 'block' }}>
          <ListItemButton
            onClick={() => {
              if (hasChildren) {
                if (item.id === 'certificates') {
                  setCertificatesExpanded(!certificatesExpanded);
                }
              } else {
                handleNavigate(item.path);
              }
            }}
            sx={{
              minHeight: 48,
              px: 2.5,
              mx: 1,
              borderRadius: 3,
              mb: 0.5,
              background: isActive
                ? `linear-gradient(135deg, ${item.color || colors.primary}20, ${item.color || colors.primary}10)`
                : 'transparent',
              border: isActive
                ? `1px solid ${item.color || colors.primary}40`
                : '1px solid transparent',
              '&:hover': {
                background: `linear-gradient(135deg, ${item.color || colors.primary}15, ${item.color || colors.primary}05)`,
                transform: 'translateX(4px)',
                boxShadow: `0 4px 12px ${item.color || colors.primary}20`,
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              pl: isChild ? 4 : 2.5,
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: open ? 2 : 'auto',
                justifyContent: 'center',
                color: isActive
                  ? item.color || colors.primary
                  : isDarkMode
                    ? '#e2e8f0'
                    : '#64748b',
                transition: 'color 0.2s ease-in-out',
              }}
            >
              <Badge
                badgeContent={item.badge}
                color="error"
                invisible={!item.badge}
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '0.6rem',
                    height: 16,
                    minWidth: 16,
                    background:
                      item.badge === 'Admin'
                        ? 'linear-gradient(135deg, #8b5cf6, #6366f1)'
                        : 'linear-gradient(135deg, #ef4444, #dc2626)',
                  },
                }}
              >
                <item.icon size={20} />
              </Badge>
            </ListItemIcon>

            {open && (
              <>
                <ListItemText
                  primary={item.label}
                  sx={{
                    opacity: open ? 1 : 0,
                    color: isActive
                      ? item.color || colors.primary
                      : isDarkMode
                        ? '#f1f5f9'
                        : '#334155',
                    '& .MuiListItemText-primary': {
                      fontWeight: isActive ? 600 : 500,
                      fontSize: '0.875rem',
                    },
                  }}
                />
                {hasChildren && (
                  <Box sx={{ color: isDarkMode ? '#94a3b8' : '#64748b' }}>
                    {isExpanded ? (
                      <ChevronDown size={16} />
                    ) : (
                      <ChevronRight size={16} />
                    )}
                  </Box>
                )}
              </>
            )}
          </ListItemButton>
        </ListItem>

        {/* Children Items */}
        {hasChildren && open && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children.map(child => renderNavigationItem(child, true))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  const drawerContent = (
    <Box
      sx={{
        height: '100%',
        background: isDarkMode
          ? 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)'
          : 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
        backdropFilter: 'blur(20px)',
        borderRight: isDarkMode
          ? '1px solid rgba(148, 163, 184, 0.1)'
          : '1px solid rgba(148, 163, 184, 0.2)',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: isDarkMode
          ? '0 10px 25px rgba(0, 0, 0, 0.5)'
          : '0 10px 25px rgba(0, 0, 0, 0.1)',
      }}
    >
      {/* Logo Section */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: open ? 'flex-start' : 'center',
          minHeight: 64,
          borderBottom: isDarkMode
            ? '1px solid rgba(148, 163, 184, 0.1)'
            : '1px solid rgba(148, 163, 184, 0.2)',
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: open ? 2 : 0,
            boxShadow: '0 8px 16px rgba(59, 130, 246, 0.3)',
          }}
        >
          <Shield size={20} color="white" />
        </Box>

        {open && (
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                lineHeight: 1.2,
                background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              COC Manager
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: isDarkMode ? '#94a3b8' : '#64748b',
                fontWeight: 500,
              }}
            >
              Certificate System
            </Typography>
          </Box>
        )}
      </Box>

      {/* User Info */}
      {open && (
        <Box
          sx={{
            p: 2,
            borderBottom: isDarkMode
              ? '1px solid rgba(148, 163, 184, 0.1)'
              : '1px solid rgba(148, 163, 184, 0.2)',
          }}
        >
          <Box
            sx={{
              p: 2,
              borderRadius: 3,
              background: isDarkMode
                ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)'
                : 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(139, 92, 246, 0.05) 100%)',
              border: isDarkMode
                ? '1px solid rgba(59, 130, 246, 0.2)'
                : '1px solid rgba(59, 130, 246, 0.1)',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Avatar
              sx={{
                width: 40,
                height: 40,
                mr: 2,
                background:
                  user?.role === 'admin'
                    ? 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)'
                    : 'linear-gradient(135deg, #06b6d4 0%, #10b981 100%)',
                fontSize: '1rem',
                fontWeight: 600,
                boxShadow:
                  user?.role === 'admin'
                    ? '0 4px 12px rgba(59, 130, 246, 0.4)'
                    : '0 4px 12px rgba(6, 182, 212, 0.4)',
              }}
            >
              {user?.FirstName?.charAt(0) || 'U'}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  lineHeight: 1.2,
                  color: isDarkMode ? '#f1f5f9' : '#1e293b',
                }}
              >
                {user?.FirstName} {user?.LastName}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                {user?.Roles === 'Admin' ? (
                  <Shield size={12} color="#3b82f6" />
                ) : (
                  <Building size={12} color="#06b6d4" />
                )}
                <Typography
                  variant="caption"
                  sx={{
                    ml: 0.5,
                    color: user?.Roles === 'Admin' ? '#3b82f6' : '#06b6d4',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    fontSize: '0.7rem',
                  }}
                >
                  {user?.Roles}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {/* Navigation Items */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 1, py: 2 }}>
        <List>{navigationItems.map(item => renderNavigationItem(item))}</List>
      </Box>
    </Box>
  );

  if (isSmallScreen) {
    return (
      <Drawer
        variant="temporary"
        open={open}
        onClose={onToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: width,
            border: 'none',
          },
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: open ? width : collapsedWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: open ? width : collapsedWidth,
          boxSizing: 'border-box',
          border: 'none',
          transition: 'width 0.3s ease-in-out',
          overflowX: 'hidden',
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default Sidebar;
